import * as React from "react";
import { FixedCommentMutation, TestAcceptabilityMutation, OverreadStatusEnum, Permission, OscillometryAnalysisSet, SessionMetadata, OverreadStatusIdEnum } from "../../misc/api/apiTypes";
import { OscillometryResultsTable } from "./OscillometryResultsTable";
import { Navigate } from "react-router";
import { useFixedComments, ToggleSessionFixedCommentsVisibilityAction } from "../Shared/UseFixedComments";
import FileAttachmentsTable from "../../file-attachments/components/viewer/FileAttachmentsTable";
import { FileAttachmentsTableMode } from "../../file-attachments/models";
import NotesPanel from "../Shared/NotesPanel";
import LoadingWithMessage from "../../misc/components/LoadingWithMessage";
import Alert, { AlertCategoryEnum } from "../../misc/components/Alert";
import SessionMetadataTable from "../Shared/SessionMetadataTable";
import AcceptabilityDashboard from "../Shared/AcceptabilityDashboard";
import { DoneLoadingAveragesValuesAction, DoneLoadingBestValuesAction, LoadingAveragesValuesAction, LoadingBestValuesAction, UpdateAveragesAction, UpdateBestTestAction, UpdateRepeatabilityAction, useAcceptability } from '../Shared/UseAcceptability';
import { PermissionEnum } from "../../misc/api/ApiPermissionEnum";
import { SessionTypes } from "../../misc/api/ApiSessionTypes";
import { OscillometryResults, StudyRelevantParameters } from "../Shared/PFTAPITypes";
import { OscillometryAnalysisSetTypeEnum } from "../../misc/api/ApiOscillometryAnalysisSetTypeEnum";
import { WebPortalClient, SessionNote } from "webportalclient";
import { useSecurityContext } from "../../Core/SecurityContext";
import { useWebPortalApiClient, useWebPortalLegacyClient } from "../../misc/api/WebPortalTokenClient";
import { GoBackToList } from "../Shared/AnalysisHelpers";
import { Authorization } from "../../misc/components/Authorization";
import { useCurrentStudyConfigurationContext } from "../../Core/CurrentStudyConfigurationContext";
import { useNavigate, useParams } from "react-router-dom";
import { ModalSize } from '../../misc/components/modal';

enum ViewState {
    View = "view",
    Overread = "overread"
}

interface ExpectedMatchParams {
    analysisSetId: string;
    viewState: ViewState;
}

export const OscillometryAnalysisScreen = () => {
    const { analysisSetId, viewState } = useParams();
    const navigate = useNavigate();
    const [sessionMetadata, setSessionMetadata] = React.useState<SessionMetadata>(null);
    const [analysisSet, setAnalysisSet] = React.useState<OscillometryResults>(null);
    const [sessionNotes, setSessionNotes] = React.useState<SessionNote[]>(null);
    const [isViewOnly, setIsViewOnly] = React.useState<boolean>(true);
    const [isSubmitting, setIsSubmitting] = React.useState<boolean>(false);
    const [error, setError] = React.useState<string | undefined>();
    const [isAnalysisRejected, setIsAnalysisRejected] = React.useState<boolean>(false);
    const [noteError, setNoteError] = React.useState<string | undefined>();
    const [studyRelevantParameters, setStudyRelevantParameters] = React.useState<StudyRelevantParameters>(null);
    const client = useWebPortalApiClient();
    const legacyClient = useWebPortalLegacyClient();

    const { currentUser, currentUserPermissions } = useSecurityContext();
    const { study } = useCurrentStudyConfigurationContext();

    const fetchSessionNotes = async (sessionId: string) => {
        const notes = await client.client.getSessionNotes(undefined, `sessionId eq ${sessionId}`);
        setSessionNotes(notes.value);
    }

    React.useEffect(() => {
        const fetchAnalysisSet = async () => {
            const access = await client.client.getOscillometryAnalysisSet(analysisSetId, "canEdit");

            setIsViewOnly(!access.canEdit);
            const overreadPermission = currentUserPermissions.find(p => p.Id == PermissionEnum.OverreadOscillometrySession);
            setIsViewOnly(!overreadPermission);

            if (access.canEdit && viewState === ViewState.Overread && overreadPermission) {
                setIsViewOnly(false);
                await legacyClient.startOscillometryOverreading(analysisSetId);
            } else {
                setIsViewOnly(true);
            }

            const results = await legacyClient.getOscillometryAnalysisSetResults(analysisSetId);
            setAnalysisSet(results);

            const sessionMetadata = await legacyClient.getOscillometrySession(results.SessionId);
            setSessionMetadata(sessionMetadata);

            const parameters = await legacyClient.getStudyOscillometryRelevantParameters(study.id);
            setStudyRelevantParameters(parameters);

            await fetchSessionNotes(results.SessionId);

            const isRejected = sessionMetadata.AnalysisSets.find(a => a.Id === analysisSetId).Status.Id === OverreadStatusIdEnum.Rejected;
            setIsAnalysisRejected(isRejected);
        }

        fetchAnalysisSet();
    }, [analysisSetId, viewState]);



    const [shouldValidateFixedComments, setShouldValidateFixedComments] = React.useState(false);

    const [fixedCommentState, fixedCommentDispatch] = useFixedComments(analysisSet, SessionTypes.Oscillometry);
    const [acceptabilityState, acceptabilityDispatch] = useAcceptability(analysisSet);

    React.useEffect(() => {

        const updateAveragesValues = async () => {
            if (acceptabilityState.spirometryResults.TestResults.length) {

                acceptabilityDispatch(LoadingAveragesValuesAction.create());

                const OscillometryBestTestValues = await legacyClient.calculateOscillometryAverageValues(acceptabilityState.spirometryResults.TestResults);

                acceptabilityDispatch(UpdateAveragesAction.create(OscillometryBestTestValues));

                acceptabilityDispatch(DoneLoadingAveragesValuesAction.create());
            }
        }
        updateAveragesValues();
    }, [acceptabilityState.changedAcceptability, acceptabilityDispatch]);

    if (analysisSet === null || sessionMetadata === null || sessionNotes === null) {
        return (<LoadingWithMessage message="Loading Oscillometry Data..." />);
    }

    const setAnalysisDecision = async (annotation: string, decision: OverreadStatusEnum) => {
        setIsSubmitting(true);
        setError(undefined);

        const sessionFixedCommentsMutation = fixedCommentState.sessionFixedComments.comments.map(x => ({ CommentId: x.id, IsSelected: x.selected } as FixedCommentMutation));
        const testFixedCommentsMutation = fixedCommentState.testFixedComments.map(x => ({
            TestId: x.id, Comments: x.comments.map(c => ({ CommentId: c.id, IsSelected: c.selected } as FixedCommentMutation)),
        }));
        const testAcceptability = acceptabilityState.spirometryResults.TestResults.map(x => ({ TestId: x.TestId, Accepted: x.OverreaderAcceptability ? true : false } as TestAcceptabilityMutation));

        try {
            const response = await legacyClient.updateAcceptabilityOscillometry(analysisSetId, decision, testAcceptability, sessionFixedCommentsMutation, testFixedCommentsMutation, annotation);

            if (response.AnalysisSetId !== analysisSetId) {
                setError("An error occurred saving the analysis set. Please contact an Administrator.");
            }

            GoBackToList(navigate, currentUserPermissions, "/Oscillometry", PermissionEnum.OverreadOscillometrySession, "/Oscillometry/Analysis");
        }
        catch (ex) {
            setError(ex.Message);
            window.scrollTo(0, 0);
        }
    }

    const addNewNote = async (annotation: string) => {
        try {
            await legacyClient.putSessionNotes(analysisSet.SessionId, annotation);
        } catch (ex) {
            setNoteError(ex.Message);
            return;
        }
        await fetchSessionNotes(analysisSet.SessionId);
    }

    const editNote = async (id: string, comment: string, annotation: string) => {
        await client.client.patchSessionNote(
            {
                comment: comment,
            }, id, annotation);
        await fetchSessionNotes(analysisSet.SessionId);
    }

    const overreadingDisabled = fixedCommentState.areAvailableFixedCommentsLoaded === false || fixedCommentState.areFixedCommentsLoaded === false;
    const hasAddingNotePermission = currentUserPermissions.some(x => x.Id == PermissionEnum.AddOscillometrySessionNotes);
    const hasViewNotePermission = currentUserPermissions.some(x => x.Id == PermissionEnum.ViewOscillometrySessionNotes);

    const pageAnalysisSet = sessionMetadata.AnalysisSets.find(x => x.Id === analysisSetId);
    const pageAnalysisSetType = pageAnalysisSet.Type.Id;
    const isQcAnalysisSet = pageAnalysisSetType === OscillometryAnalysisSetTypeEnum.QC;
    const isSiteAnalysisSet = pageAnalysisSetType === OscillometryAnalysisSetTypeEnum.Site;
    const isOverreadAnalysisSet = pageAnalysisSetType === OscillometryAnalysisSetTypeEnum.Overread;
    const isUnauthorized = isQcAnalysisSet && pageAnalysisSet.Analyst.Username && pageAnalysisSet.Analyst.Username != currentUser.Username;

    if (isUnauthorized) {
        return <Navigate to={{ pathname: "/unauthorized" }} />;
    }

    if (viewState === ViewState.Overread && isSiteAnalysisSet) {
        return <Navigate to={{ pathname: `/Oscillometry/Analysis/${analysisSetId}/view` }} />;
    }
    return (
        <>
            <Alert show={overreadingDisabled} category={AlertCategoryEnum.Danger}>
                Over-reading disabled. Fixed comments have not been configured for this study, please ask the Administrator to configure these first.
            </Alert>

            <Alert show={error !== undefined} category={AlertCategoryEnum.Danger}>
                {error}
            </Alert>

            <h2 className="text-primary">Oscillometry Data</h2>
            {!isQcAnalysisSet ? <SessionMetadataTable
                sessionMetadata={sessionMetadata}
                selectedAnalysisSetId={analysisSetId}
            /> : <Alert show={true} category={AlertCategoryEnum.Info}>Session Information is hidden from view in this workflow</Alert>}

            <h2 className="text-primary">Results</h2>
            <div className="panel panel-default">
                <div className="panel-body">
                    <OscillometryResultsTable
                        isViewOnly={isViewOnly || overreadingDisabled || acceptabilityState.loadingBestValues}
                        isSiteAnalysisSet={isSiteAnalysisSet}
                        resultData={acceptabilityState.spirometryResults}
                        fixedCommentsState={fixedCommentState}
                        fixedCommentsDispatch={fixedCommentDispatch}
                        shouldValidateFixedComments={shouldValidateFixedComments}
                        acceptabilityState={acceptabilityState}
                        acceptabilityDispatch={acceptabilityDispatch}
                        isRejected={isAnalysisRejected}
                        studyOscillometryRelevantParameters={studyRelevantParameters}
                    />
                </div>
            </div>
            <Authorization studyId={study.id} currentPermissions={currentUserPermissions} permissionId={PermissionEnum.AccessOscillometryAnalysisSetsAttachment}>
                <h2 className="text-primary">Attachments</h2>
                <div className="panel panel-default">
                    <div className="panel-body">
                        <FileAttachmentsTable
                            legacyClient={legacyClient}
                            fileAttachments={analysisSet.Attachments.map(a => ({ id: a.Id, fileSizeInBytes: a.FileSizeInBytes, fileName: a.FileName }))}
                            mode={FileAttachmentsTableMode.ListOnly}
                        />
                    </div>
                </div>
            </Authorization>
            {
                hasViewNotePermission ?
                    <NotesPanel
                        buttonDisabled={sessionMetadata.AnalysisSets.find(a => a.Id === analysisSetId).Type.Id === OscillometryAnalysisSetTypeEnum.QC}
                        header="Session Notes"
                        showNotesCount={true}
                        isAddingNotesEnabled={!isViewOnly && !overreadingDisabled && hasAddingNotePermission && isOverreadAnalysisSet}
                        notes={sessionNotes}
                        errorStatus={noteError}
                        modalSize={ModalSize.Large}
                        onAddNote={(annotation) => addNewNote(annotation)}
                        onEditNote={(id, comment, annotation) => editNote(id, comment, annotation)}
                    />
                    : null
            }
            <AcceptabilityDashboard
                isViewOnly={isViewOnly || overreadingDisabled}
                analysisSetId={analysisSetId}
                fixedCommentsDispatch={fixedCommentDispatch}
                fixedCommentState={fixedCommentState}
                acceptabilityState={acceptabilityState}
                sessionType={SessionTypes.Oscillometry}
                overreadStatus={sessionMetadata.AnalysisSets.find(a => a.Id === analysisSetId).Status.Id}
                onNoSessionCommentsSelected={() => {
                    fixedCommentDispatch(ToggleSessionFixedCommentsVisibilityAction.create());
                }}
                onAcceptabilitySelected={(annotation, decision) => setAnalysisDecision(annotation, decision)}
                onCancel={() => GoBackToList(navigate, currentUserPermissions, "/Oscillometry", PermissionEnum.OverreadOscillometrySession, "/Oscillometry/Analysis")}
            />
        </>
    );
};
